import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { Alert } from './Alert';
import { SkeletonRows } from './Skeleton';

/* The three states every screen must handle, as components rather than as a discipline.
 *
 * **`ErrorState` exists because of one rule in the brief: never show "request failed with 500" as the
 * only explanation.** The technical detail is not deleted — an engineer needs it and hiding it is how
 * a support ticket becomes a bisect — it is *demoted* behind a disclosure, under a sentence a data
 * steward can act on. The component takes those two things separately and will not render without
 * both, so the useful sentence cannot be forgotten in favour of the exception message.
 *
 * The pairing is what matters. `title` says what could not be done, in the reader's terms
 * ("The source list could not be loaded"); `guidance` says what to do about it; `detail` carries
 * whatever the server actually said.
 */

export interface ErrorStateProps {
  /** What could not be done, in the reader's terms. Not the exception. */
  readonly title: string;
  /** What the reader can do next. Required — an error with no next step is a dead end. */
  readonly guidance: string;
  /** The technical detail, shown only when the reader asks for it. */
  readonly detail?: string | undefined;
  /** A retry, where retrying is meaningful. */
  readonly action?: ReactNode;
  /** The disclosure's label, e.g. "Technical detail". */
  readonly detailLabel?: string;
}

export function ErrorState({
  title,
  guidance,
  detail,
  action,
  detailLabel = 'Technical detail',
}: ErrorStateProps): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Alert tone="error" title={title}>
      <p style={{ margin: 0 }}>{guidance}</p>
      {action !== undefined ? (
        <div style={{ marginTop: 'var(--space-3)' }}>{action}</div>
      ) : null}
      {detail !== undefined ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          {/* A real button rather than `<details>`: the summary marker's styling is not consistent
              across engines, and this needs to look like the rest of the system. */}
          <button
            type="button"
            className="lq-link-button"
            onClick={() => {
              setOpen(!open);
            }}
            aria-expanded={open}
          >
            {open ? `Hide ${detailLabel.toLowerCase()}` : detailLabel}
          </button>
          {open ? <pre className="lq-diagnostic">{detail}</pre> : null}
        </div>
      ) : null}
    </Alert>
  );
}

/* A loading state that reserves the shape of what is coming, rather than a spinner in the middle of an
 * empty page. The difference is that the layout does not jump when the data lands, which on a dense
 * table is the difference between reading and re-finding your place. */
export function LoadingState({
  rows = 5,
  label,
}: {
  readonly rows?: number;
  readonly label: string;
}): ReactElement {
  return (
    <div role="status" aria-label={label} aria-busy="true">
      <SkeletonRows rows={rows} />
    </div>
  );
}

/* Neither a failure nor an absence of data: the deployment cannot answer this question at all, because
 * a capability is not configured. It is `notice` rather than `error` on purpose — nothing is broken and
 * nobody needs to be paged — and it names the operator action, because the reader of this screen is
 * usually not the person who can fix it.
 *
 * This is the state the platform's own `/readiness` vocabulary calls MISSING, and having a component
 * for it is what stops a screen reporting an unconfigured capability as a fault. */
export function UnavailableState({
  title,
  guidance,
}: {
  readonly title: string;
  readonly guidance: string;
}): ReactElement {
  return (
    <Alert tone="notice" title={title}>
      {guidance}
    </Alert>
  );
}
